import React, { useState, useMemo } from 'react';
import { FileText, BookOpen, Edit, Trash2, Download, RotateCcw, Undo2, ChevronDown, ChevronRight, Plus, Sparkles } from 'lucide-react';
import { formatNumber } from '../utils';
import { useLanguage } from '../contexts/LanguageContext';
import { Toast } from './Toast';

export function BitacoraTable({ 
  title, 
  entries, 
  onDelete, 
  onEdit, 
  onReset,
  onUndo,
  canUndo = false,
  onNewEntry,
  onAiImport,
  variant = 'blue' 
}) {
  const { t, language } = useLanguage();
  const accentColor = variant === 'blue' ? 'text-blue-600' : 'text-red-600';
  const headerIcon = variant === 'blue' ? <FileText className={accentColor} /> : <BookOpen className={accentColor} />;
  const [confirmReset, setConfirmReset] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const toggleExpand = (id) => setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  // Always show trades sorted by date, newest entry on top (tie-break by first purchase time)
  const sortedEntries = useMemo(() => {
    const ts = (e) => {
      const d = new Date(e.date);
      if (isNaN(d.getTime())) return 0;
      const [h = 0, m = 0, s = 0] = String(e.purchases?.[0]?.time || e.time || '00:00:00').split(':').map(Number);
      return d.getTime() + ((h * 3600 + m * 60 + (s || 0)) * 1000);
    };
    return [...entries].sort((a, b) => ts(b) - ts(a));
  }, [entries]);

  const downloadAsCSV = () => {
    if (entries.length === 0) {
      setToastMessage(language === 'es' ? '⚠️ No hay datos para descargar' : '⚠️ No data to download');
      setToastType('error');
      setShowToast(true);
      return;
    }

    setIsDownloading(true);

    try {
      const headers = [
        language === 'es' ? 'Fecha' : 'Date',
        language === 'es' ? 'Hora' : 'Time',
        language === 'es' ? 'Símbolo' : 'Symbol',
        'Strike',
        language === 'es' ? 'Tipo' : 'Type',
        language === 'es' ? 'Cantidad' : 'Quantity',
        language === 'es' ? 'Precio Compra' : 'Buy Price',
        language === 'es' ? 'Inversión Total' : 'Total Investment',
        language === 'es' ? 'Precio Límite' : 'Limit Price',
        language === 'es' ? 'Fecha Venta' : 'Sell Date',
        language === 'es' ? 'Hora Venta' : 'Sell Time',
        language === 'es' ? 'Precio Venta' : 'Sell Price',
        language === 'es' ? 'Venta Total' : 'Total Sale',
        'Fees',
        'Profit',
        '% Profit',
        'TD',
        language === 'es' ? 'Notas' : 'Notes'
      ];

      const rows = sortedEntries.map(entry => [
        entry.date || '',
        entry.time || '',
        entry.ticker || entry.symbol || '',
        entry.strike || '',
        entry.type || '',
        entry.qty || '',
        entry.buyPrice || '',
        entry.totalInvested || '',
        entry.limitPrice || '',
        entry.sellDate || '',
        entry.sellTime || '',
        entry.sellPrice || '',
        entry.totalSale || '',
        ((entry.brokerFee || 0) + (entry.miscFee || 0)) || '',
        entry.profit || '',
        entry.profitPercent || '',
        entry.td || '',
        (entry.notes || '').replace(/,/g, ';')
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      // Add BOM for UTF-8 to ensure proper Excel encoding
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const fileName = variant === 'blue' 
        ? `bitacora_${new Date().toISOString().split('T')[0]}.csv`
        : `estudio_${new Date().toISOString().split('T')[0]}.csv`;
      
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      
      // Add to DOM to ensure download works in all browsers
      document.body.appendChild(link);
      link.click();
      
      // Show success toast
      setTimeout(() => {
        setToastMessage(language === 'es' 
          ? `✅ Archivo descargado: ${fileName}` 
          : `✅ File downloaded: ${fileName}`);
        setToastType('success');
        setShowToast(true);
      }, 200);
      
      // Cleanup with delay
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        setIsDownloading(false);
      }, 500);
      
      console.log(`[SUCCESS] CSV downloaded: ${fileName}`);
    } catch (error) {
      console.error('[ERROR] CSV download failed:', error);
      setIsDownloading(false);
      setToastMessage(language === 'es' 
        ? '❌ Error al descargar archivo' 
        : '❌ Download failed');
      setToastType('error');
      setShowToast(true);
    }
  };

  const handleReset = () => {
    console.log('[DEBUG] Reset confirmed by user');
    
    if (confirmReset) {
      onReset && onReset();
      setConfirmReset(false);
      
      // Show success toast
      setToastMessage(language === 'es' 
        ? '✅ Datos reiniciados correctamente' 
        : '✅ Data reset successfully');
      setToastType('success');
      setShowToast(true);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid={variant === 'blue' ? 'bitacora-table' : 'estudio-table'}>
      
      {/* Toast Notification */}
      {showToast && (
        <Toast 
          message={toastMessage}
          type={toastType}
          onClose={() => setShowToast(false)}
          duration={4000}
        />
      )}
      
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-white p-3 sm:p-4 rounded-xl shadow-sm border border-gray-100 relative">
        
        {/* Reset Confirmation Modal (like calendar) */}
        {confirmReset && (
          <div className="absolute inset-0 z-50 bg-white/90 backdrop-blur-sm flex items-center justify-center p-4 rounded-xl">
            <div className="bg-white p-6 rounded-xl shadow-2xl border-2 border-gray-200 max-w-sm w-full text-center space-y-4">
              <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto">
                <RotateCcw size={24} />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-bold text-gray-900">
                  {language === 'es' 
                    ? `¿Reiniciar ${variant === 'blue' ? 'Bitácora' : 'Estudio'}?` 
                    : `Reset ${variant === 'blue' ? 'Logbook' : 'Study'}?`}
                </h3>
                <p className="text-sm text-gray-500">
                  {language === 'es' 
                    ? 'Esta acción se puede deshacer.' 
                    : 'This action can be undone.'}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmReset(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-lg transition-colors text-sm"
                >
                  {language === 'es' ? 'Cancelar' : 'Cancel'}
                </button>
                <button
                  onClick={handleReset}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition-colors text-sm"
                >
                  {language === 'es' ? 'Confirmar' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        <h2 className="text-lg sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
          {headerIcon} {title}
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          {onNewEntry && (
            <button
              onClick={onNewEntry}
              className="flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-bold text-white bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 rounded-lg shadow-md hover:shadow-lg transition-all transform active:scale-95"
              data-testid="bitacora-new-entry-btn"
            >
              <Plus size={16} />
              {language === 'es' ? 'Nueva Entrada' : 'New Entry'}
            </button>
          )}
          {onAiImport && (
            <button
              onClick={onAiImport}
              className="flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-bold text-white bg-gradient-to-br from-violet-500 to-violet-600 hover:from-violet-600 hover:to-violet-700 rounded-lg shadow-md hover:shadow-lg transition-all transform active:scale-95"
              title={language === 'es' ? 'Rellenar una entrada leyendo una captura con IA' : 'Fill an entry by reading a screenshot with AI'}
              data-testid="bitacora-ai-import-btn"
            >
              <Sparkles size={16} />
              {language === 'es' ? 'Leer con IA' : 'Read with AI'}
            </button>
          )}
          {canUndo && (
            <button
              onClick={onUndo}
              className="flex items-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-semibold text-amber-700 bg-gradient-to-br from-amber-50 to-amber-100 hover:from-amber-100 hover:to-amber-200 rounded-lg border border-amber-200 transition-all shadow-sm hover:shadow-md active:scale-95"
              data-testid="undo-btn"
            >
              <Undo2 size={16} />
              {language === 'es' ? 'Deshacer' : 'Undo'}
            </button>
          )}
          {onReset && entries.length > 0 && (
            <button
              onClick={() => setConfirmReset(true)}
              className="flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-bold text-white bg-gradient-to-br from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-lg shadow-md hover:shadow-lg transition-all transform active:scale-95"
              title={language === 'es' ? 'Reiniciar datos' : 'Reset data'}
              data-testid="reset-btn"
            >
              <RotateCcw size={16} className="sm:w-[18px] sm:h-[18px]" />
              <span>{language === 'es' ? 'Reiniciar' : 'Reset'}</span>
            </button>
          )}
          <button
            onClick={downloadAsCSV}
            disabled={entries.length === 0 || isDownloading}
            className={`flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-bold rounded-lg shadow-md transition-all transform active:scale-95 ${
              isDownloading 
                ? 'bg-gradient-to-br from-emerald-100 to-emerald-200 text-emerald-700 cursor-wait' 
                : entries.length === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-60'
                  : 'bg-gradient-to-br from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white hover:shadow-lg'
            }`}
            title={language === 'es' ? 'Descargar archivo CSV' : 'Download CSV file'}
            data-testid="download-csv-btn"
          >
            <Download size={16} className={`sm:w-[18px] sm:h-[18px] ${isDownloading ? 'animate-bounce' : ''}`} />
            <span>
              {isDownloading 
                ? (language === 'es' ? 'Descargando...' : 'Downloading...') 
                : (language === 'es' ? 'Descargar CSV' : 'Download CSV')}
            </span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        {/* Scroll Hint for Mobile */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-2 flex items-center justify-center gap-2 sm:hidden border-b border-blue-100">
          <svg className="w-4 h-4 text-blue-600 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
          </svg>
          <span className="text-xs font-semibold text-blue-700">
            {language === 'es' ? 'Desliza para ver más →' : 'Swipe to see more →'}
          </span>
        </div>
        
        {/* Scrollable container with touch support */}
        <div 
          className="overflow-x-auto overflow-y-visible relative"
          style={{
            WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
            overscrollBehaviorX: 'contain' // Prevent scroll chaining
          }}
        >
          {/* Shadow indicators for scroll */}
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent pointer-events-none z-10 sm:hidden" />
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none z-10 sm:hidden" />
          
          <table className="min-w-full text-[9px] sm:text-[11px] divide-y divide-gray-200" style={{ minWidth: '800px' }}>
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap">{t.bitacora.date}</th>
                <th className="px-2 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap">{t.bitacora.time}</th>
                <th className="px-2 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap">{t.bitacora.symbol}</th>
                <th className="px-2 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap hidden sm:table-cell">{t.bitacora.strike}</th>
                <th className="px-2 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap">{t.bitacora.type}</th>
                <th className="px-2 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap">{t.bitacora.qty}</th>
                <th className="px-2 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap hidden lg:table-cell">{t.bitacora.buyPrice}</th>
                <th className="px-2 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap hidden lg:table-cell">{t.bitacora.totalInvested}</th>
                <th className="px-2 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap hidden xl:table-cell">{t.bitacora.limitPrice}</th>
                <th className="px-2 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap hidden xl:table-cell">{t.bitacora.sellDate}</th>
                <th className="px-2 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap hidden xl:table-cell">{t.bitacora.sellTime}</th>
                <th className="px-2 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap hidden xl:table-cell">{t.bitacora.sellPrice}</th>
                <th className="px-2 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap hidden xl:table-cell">{t.bitacora.totalSale}</th>
                <th className="px-2 py-2 sm:py-3 text-left font-bold text-amber-600 uppercase whitespace-nowrap hidden lg:table-cell">Fees</th>
                <th className="px-2 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap">{t.bitacora.profit}</th>
                <th className="px-2 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap hidden sm:table-cell">{t.bitacora.profitPercent}</th>
                <th className="px-2 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap hidden lg:table-cell">{t.bitacora.td}</th>
                <th className="px-2 py-2 sm:py-3 text-left font-bold text-gray-500 uppercase whitespace-nowrap hidden xl:table-cell">{t.bitacora.notes}</th>
                <th className="px-2 py-2 sm:py-3 text-right font-bold text-gray-500 uppercase whitespace-nowrap"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {entries.length === 0 ? (
                <tr>
                  <td colSpan={19} className="px-4 py-8 text-center text-gray-400 italic">{t.bitacora.noEntries}</td>
                </tr>
              ) : (
                sortedEntries.map((entry) => {
                  const hasMultiplePurchases = entry.purchases?.length > 1;
                  const isExpanded = !!expandedRows[entry.id];
                  return (
                  <React.Fragment key={entry.id}>
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-2 py-2 sm:py-3 whitespace-nowrap">{entry.date}</td>
                    <td className="px-2 py-2 sm:py-3 whitespace-nowrap">{entry.time}</td>
                    <td className="px-2 py-2 sm:py-3 whitespace-nowrap font-bold">
                      <span className="inline-flex items-center gap-1">
                        {hasMultiplePurchases && (
                          <button
                            onClick={() => toggleExpand(entry.id)}
                            className="text-amber-600 hover:text-amber-800 p-0.5"
                            title={language === 'es' ? 'Ver compras individuales' : 'View individual purchases'}
                            data-testid={`expand-purchases-${entry.id}`}
                          >
                            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </button>
                        )}
                        {entry.ticker || entry.symbol}
                        {hasMultiplePurchases && (
                          <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full text-[8px] sm:text-[9px] font-black">
                            ×{entry.purchases.length}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-2 py-2 sm:py-3 whitespace-nowrap hidden sm:table-cell">{entry.strike?.toFixed(1)}</td>
                    <td className={`px-2 py-2 sm:py-3 whitespace-nowrap font-bold ${entry.type === 'Call' ? 'text-green-600' : 'text-red-600'}`}>
                      {entry.type?.toUpperCase()}
                    </td>
                    <td className="px-2 py-2 sm:py-3 whitespace-nowrap">{entry.qty}</td>
                    <td className="px-2 py-2 sm:py-3 whitespace-nowrap hidden lg:table-cell">
                      ${formatNumber(entry.buyPrice)}
                      {hasMultiplePurchases && (
                        <span className="text-[8px] text-amber-600 font-bold ml-1">{language === 'es' ? 'prom.' : 'avg.'}</span>
                      )}
                    </td>
                    <td className="px-2 py-2 sm:py-3 whitespace-nowrap font-medium hidden lg:table-cell">${formatNumber(entry.totalInvested)}</td>
                    <td className="px-2 py-2 sm:py-3 whitespace-nowrap hidden xl:table-cell">${formatNumber(entry.limitPrice)}</td>
                    <td className="px-2 py-2 sm:py-3 whitespace-nowrap hidden xl:table-cell">{entry.sellDate}</td>
                    <td className="px-2 py-2 sm:py-3 whitespace-nowrap hidden xl:table-cell">{entry.sellTime}</td>
                    <td className="px-2 py-2 sm:py-3 whitespace-nowrap hidden xl:table-cell">${formatNumber(entry.sellPrice)}</td>
                    <td className="px-2 py-2 sm:py-3 whitespace-nowrap font-medium hidden xl:table-cell">${formatNumber(entry.totalSale)}</td>
                    <td className="px-2 py-2 sm:py-3 whitespace-nowrap font-medium text-amber-700 hidden lg:table-cell" data-testid={`fees-${entry.id}`}>
                      {((entry.brokerFee || 0) + (entry.miscFee || 0)) > 0 ? `$${formatNumber((entry.brokerFee || 0) + (entry.miscFee || 0))}` : '-'}
                    </td>
                    <td className={`px-2 py-2 sm:py-3 whitespace-nowrap font-bold ${entry.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ${formatNumber(entry.profit)}
                    </td>
                    <td className={`px-2 py-2 sm:py-3 whitespace-nowrap font-bold hidden sm:table-cell ${entry.profitPercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatNumber(entry.profitPercent)}%
                    </td>
                    <td className="px-2 py-2 sm:py-3 whitespace-nowrap hidden lg:table-cell">{entry.td}</td>
                    <td className="px-2 py-2 sm:py-3 whitespace-nowrap italic text-gray-400 hidden xl:table-cell max-w-[100px] truncate">{entry.notes}</td>
                    <td className="px-2 py-2 sm:py-3 whitespace-nowrap text-right space-x-1 sm:space-x-2">
                      <button onClick={() => onEdit(entry)} className="text-blue-400 hover:text-blue-600 p-1" data-testid={`edit-entry-${entry.id}`}>
                        <Edit size={14} />
                      </button>
                      <button onClick={() => onDelete(entry.id)} className="text-red-400 hover:text-red-600 p-1" data-testid={`delete-entry-${entry.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                  {hasMultiplePurchases && isExpanded && (
                    <tr className="bg-amber-50/60" data-testid={`purchases-detail-${entry.id}`}>
                      <td colSpan={19} className="px-4 py-3">
                        <div className="text-[10px] sm:text-xs">
                          <p className="font-bold text-amber-800 uppercase mb-2">
                            {language === 'es' ? 'Compras individuales (promediado)' : 'Individual purchases (averaged)'}
                          </p>
                          <div className="grid gap-1">
                            {entry.purchases.map((p, i) => (
                              <div key={`${entry.id}-p-${i}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 bg-white border border-amber-200 rounded-md px-3 py-1.5">
                                <span className="font-black text-amber-700">#{i + 1}</span>
                                <span><span className="text-gray-400 font-semibold">{language === 'es' ? 'Hora:' : 'Time:'}</span> {p.time || '-'}</span>
                                <span><span className="text-gray-400 font-semibold">{t.bitacora.qty}:</span> {p.qty}</span>
                                <span><span className="text-gray-400 font-semibold">{t.bitacora.buyPrice}:</span> ${formatNumber(p.buyPrice)}</span>
                                <span className="font-bold text-gray-700"><span className="text-gray-400 font-semibold">Subtotal:</span> ${formatNumber(p.qty * p.buyPrice * 100)}</span>
                              </div>
                            ))}
                          </div>
                          <p className="mt-2 font-bold text-indigo-700">
                            {language === 'es' ? 'Promedio' : 'Average'}: ${formatNumber(entry.buyPrice)} · {language === 'es' ? 'Cantidad total' : 'Total qty'}: {entry.qty} · {language === 'es' ? 'Invertido' : 'Invested'}: ${formatNumber(entry.totalInvested)}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
